import ArrowBack from '@material-ui/icons/ArrowBack';
import { OriginCard } from 'components/bgp-overview/OriginCard';
import { PrefixRow } from 'components/bgp-overview/PrefixRow';
import { UpstreamsRow } from 'components/bgp-overview/UpstreamsRow';
import { orderBy, sumBy, uniq } from 'lodash';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import { useQueries, useQuery, UseQueryResult } from 'react-query';
import { getAnnouncedPrefixes } from 'ripe/announcedPrefixes';
import { getAsName } from 'ripe/asName';
import { ASN, Prefix } from 'ripe/common';
import { getPrefixVisibility, PrefixVisibility } from 'upx-tools/prefix-visibility';
import UpxLogo from '../../assets/upx.svg';

export default function BgpOverview({ asn, name }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter();
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const itemsPerPage = 32;

  const { data: announcedPrefixes } = useQuery(
    ['announced-prefixes', asn],
    () => getAnnouncedPrefixes(asn),
    {
      refetchInterval: 3000,
    },
  );

  const filteredPrefixes = React.useMemo(() => {
    if (!announcedPrefixes) return undefined;
    if (!search) return announcedPrefixes;
    return announcedPrefixes.filter((prefix) => prefix.includes(search));
  }, [announcedPrefixes, search]);

  const paginatedPrefixes = React.useMemo(() => {
    if (!filteredPrefixes) return undefined;
    const start = (page - 1) * itemsPerPage;
    return filteredPrefixes.slice(start, start + itemsPerPage);
  }, [filteredPrefixes, page]);

  const totalPages = filteredPrefixes ? Math.ceil(filteredPrefixes.length / itemsPerPage) : 0;

  const upstreamAsns = useVisibileUpstreamAsns(paginatedPrefixes ?? []);

  return (
    <>
      <Head>
        <title>{name} - UPX Tools</title>
      </Head>

      <main className="container flex flex-col p-2 sm:p-4 min-w-min mx-auto text-sm">
        <div className="flex flex-row items-center">
          <UpxLogo className="transform scale-50 -m-14 sm:scale-60 sm:-m-11" />
          <span className="m-2 text-3xl sm:text-4xl font-roboto text-blue">|</span>
          <span className="flex-grow font-saira text-2xl sm:text-3xl">Tools</span>
        </div>

        <div className="flex justify-between items-center my-2 text-blue">
          <button className="inline-flex items-center" onClick={() => router.push('/')}>
            <ArrowBack fontSize="large" />
            <div className="p-4 text-base">Upstream visibility</div>
          </button>

          <input
            type="text"
            placeholder="Search prefix..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 rounded bg-white-darkest text-black border border-gray-dark focus:outline-none focus:border-blue"
          />
        </div>

        {paginatedPrefixes && (
          <>
            <div className="flex flex-col sticky top-0 min-w-min">
              <div className="static">
                <div className="flex flex-row flex-nowrap bg-gray-dark">
                  <OriginCard asn={asn} name={name} />
                  <UpstreamsRow upstreamAsns={upstreamAsns} />
                </div>
                <div className="bg-gradient-to-b from-gray-dark w-full h-6 absolute top-30"></div>
              </div>
            </div>

            <div className="flex flex-col">
              {paginatedPrefixes.map((prefix) => {
                return <PrefixRow key={prefix} prefix={prefix} originAsn={asn} upstreamAsns={upstreamAsns} />;
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center py-6 space-x-4">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="px-4 py-2 bg-gray-darkest text-white rounded border border-gray-dark disabled:opacity-50 transition-opacity hover:opacity-80"
                >
                  Previous
                </button>
                <span className="text-white text-base">
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  className="px-4 py-2 bg-gray-darkest text-white rounded border border-gray-dark disabled:opacity-50 transition-opacity hover:opacity-80"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const resource = Number(context.params!['resource']);

  return {
    props: {
      asn: resource,
      name: await getAsName(resource),
    },
  };
};

const useVisibileUpstreamAsns = (prefixes: Prefix[]) => {
  const prefixVisibilityResults = useQueries(
    prefixes.map((prefix) => {
      return {
        queryKey: ['prefix-visibility', prefix],
        queryFn: () => getPrefixVisibility(prefix),
      };
    }),
  ) as UseQueryResult<PrefixVisibility>[];

  const loadedPrefixVisibilities = prefixVisibilityResults
    .filter((result) => result.isSuccess)
    .map((result) => result.data!);

  // Get visibile upstream ASNs removing duplicates
  const upstreamAsns = uniq(
    loadedPrefixVisibilities.flatMap((prefixVisibility) => Object.keys(prefixVisibility.routeCountByUpstreamAsn)),
  ).map(Number);

  // Sums the route count of a upstream in all prefixes
  const getRouteCountByUpstreamAsn = (asn: ASN) =>
    sumBy(loadedPrefixVisibilities, (row) => row.routeCountByUpstreamAsn[asn] ?? 0);

  // Order by upstream with highest visibility
  return orderBy(upstreamAsns, getRouteCountByUpstreamAsn, 'desc');
};
